import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { CognitoUserSession } from "amazon-cognito-identity-js";
import { Auth } from "aws-amplify";
import { getConfigValue } from "utils/config";
import { dateTimeTransformLink } from "./dateTimeLink";

type Extensions = {
  traceId?: string;
};

const cache = new InMemoryCache({
  typePolicies: {
    // Define how Apollo identifies unique objects for proper caching
    Solution: {
      keyFields: ["key"],
      fields: {
        // Merge access fields from different queries
        access: {
          merge: true
        }
      }
    },
    SolutionAccessViewModel: {
      merge: true
    },
    ScopedUser__SolutionScope: {
      keyFields: ["name"] // Solution-scoped users identified by name
    },
    User: {
      keyFields: ["name"] // From users.allUsers and tenant.access.users queries
    },
    ScopedUser__TenantScope: {
      keyFields: ["name"] // For tenant-scoped users
    },
    ApiKey: {
      keyFields: ["id"] // For all ApiKey objects (from publicApi.apiKeys queries)
    },
    Workspace: {
      keyFields: ["id"] // From workspaces.workspaces query
    },
    ConfigurationKey: {
      keyFields: ["name"] // From configuration.keys query
    },
    // Merge policies for nested objects
    ProjectsViewModel: { merge: true },
    Solutions: { merge: true },
    PublicApi: { merge: true },
    UsersViewModel: { merge: true },
    WorkspacesViewModel: { merge: true }
  }
});

const errorLink = onError(({ operation, response }) => {
  const fetchResponse = operation.getContext().response;

  // Skip trace ID augmentation in case of mocked requests
  if (!fetchResponse) return;

  const headerName = "x-amzn-trace-id";
  const { headers } = fetchResponse;
  const traceId = headers.get(headerName);
  if (response && response.errors && traceId) {
    response.errors.forEach(error => {
      if (!error.extensions)
        (error as { extensions: Extensions }).extensions = {};
      (error.extensions as Extensions)["traceId"] = traceId;
    });
  }
});

const authLink = setContext(
  (request, res) =>
    new Promise(async (resolve, reject) => {
      const userSession: CognitoUserSession | null =
        await Auth.currentSession();
      const token = userSession?.getIdToken()?.getJwtToken();
      resolve({
        headers: {
          Authorization: token
        }
      });
    })
);
const httpLink = new HttpLink({ uri: getConfigValue("GRAPHQL_ENDPOINT") });

export const defaultWatchQueryFetchPolicy = "cache-and-network";

const client = new ApolloClient({
  cache,
  link: ApolloLink.from([
    errorLink,
    dateTimeTransformLink,
    authLink.concat(httpLink)
  ]),
  // https://www.apollographql.com/docs/react/api/apollo-client/#example-defaultoptions-object
  // The apollo docs state that the <Query/> component use watchQuery default
  // options. From testing the useQuery Hook does the same, thus watchQuery has
  // to be used in order to change default options for the useQuery Hook.
  defaultOptions: {
    mutate: {
      // https://github.com/apollographql/apollo-client/issues/4577#issuecomment-472791200
      // Apollo docs actually suggest to not set a fetch policy for mutations as
      // the only option which does not throw an exception is "no-cache"
      errorPolicy: "all"
    },
    // Use cache-and-network to show cached data immediately while fetching fresh data in background.
    // This ensures UI is responsive (shows cache first) but data stays up-to-date.
    // For static/rarely-changing data (like solution names), use cache-first explicitly in the query.
    watchQuery: {
      fetchPolicy: defaultWatchQueryFetchPolicy,
      errorPolicy: "all",
      notifyOnNetworkStatusChange: false
    },
    query: {
      fetchPolicy: "network-only",
      errorPolicy: "all",
      notifyOnNetworkStatusChange: false
    }
  }
});

function clientFactory() {
  // reuse the same client for now to ensure caching is used properly, might be clarified if this must be a singleton
  return client;
}

export default clientFactory;
